import { message } from "antd";
import { useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Candidate from "redux/models/candidate";
import { setCandidateList } from "redux/slices/candidateSlice";
import { selectUser } from "redux/slices/userSlice";
import K from "utilities/constants";
import { getColumns } from "./columns";
import { ServerSideDataGrid } from "../../../common/components/serverSideDataGrid/serverSideDataGrid";
import {
  checkStatusExist,
  isPermissionPresent,
} from "../../../utilities/generalUtility";

const pageSize = 25;

export default function CandidateListing({
  setCandidateActivity,
  setModalVisible,
  search,
  gridRef,
  setSelectedData,
  refreshTable,
}) {
  const statusRef = useRef(null);
  const selectedRowRef = useRef(0);

  const getRowStyle = (params) => {
    if (params.data?.isConsiderable === 0) {
      return { opacity: 0.5 }; // Disable row
    }

    return null;
  };

  const isRowSelectable = (params) => {
    return params.data?.isConsiderable === 1;
  };

  const sortRef = useRef({ sortCol: "candidate_job_entry.id", sort: "asc" });
  const dispatch = useDispatch();

  const userSlice = useSelector(selectUser);
  const userDetails = userSlice.details;

  const canViewCandidates = isPermissionPresent(
    K.Permissions.ViewCandidates,
    userSlice.roles,
  );

  const { jobId, statusesId, statusName, subStatusesId, subStatusName } =
    useParams();

  const showModalViewActivity = () => {
    setModalVisible((prev) => {
      return {
        ...prev,
        isActivityModalVisible: true,
      };
    });
  };

  const fetchCandidateDetails = async (params) => {
    try {
      const res = await Candidate.getActivity(params.data.id);

      if (selectedRowRef.current === 1 || params.data.isConsiderable === 0) {
        setCandidateActivity(res);
        showModalViewActivity();
      }
    } catch (err) {
      message.error("Failed to fetch candidate!");
      console.error(err);
    }
  };

  const columns = getColumns();

  const onRowClicked = (event) => {
    event.api.deselectAll();
    event.node.setSelected(true);
    selectedRowRef.current = 1;
    fetchCandidateDetails(event);
  };

  const onSelectionChanged = (event) => {
    const selectedRows = event.api.getSelectedRows();

    setSelectedData((prev) => ({
      ...prev,
      isDisabled: selectedRows.length === 0,
      candidateJobEntryIds: selectedRows.map(({ id }) => id),
    }));
  };

  const onRowSelected = (params) => {
    let selectedRows = [];
    let candidateJobEntryId = [];
    let isDisabled = true;

    selectedRows = params.api.getSelectedRows();

    candidateJobEntryId = selectedRows.map((item) => item.id);
    if (selectedRows >= 1) {
      isDisabled = false;
    }
    if (selectedRows.length > 1) {
      setCandidateActivity(null);
    }
    setSelectedData((prev) => ({
      ...prev,
      candidateJobEntryIds: candidateJobEntryId,
      isDisabled,
    }));

    selectedRowRef.current = selectedRows.length;
  };

  const getServerSideData = useCallback(
    (options) => {
      return Candidate.v2.getByJobId(jobId, {
        ...options,
        search: search || undefined,
        filter: statusRef.current,
        pageSize,
      });
    },
    [search],
  );

  const onGridDataFetchSuccess = useCallback(
    (data) => {
      dispatch(setCandidateList(data.candidate ?? []));
    },
    [dispatch],
  );

  useEffect(() => {
    if (statusesId && checkStatusExist(statusName, subStatusName)) {
      statusRef.current = {
        statusId: statusesId || null,
        subStatusId: subStatusesId || null,
      };
    } else {
      statusRef.current = null;
    }
    refreshTable();
  }, [statusesId, subStatusesId]);

  if (!canViewCandidates) return null;

  return (
    <ServerSideDataGrid
      ref={gridRef}
      userId={userDetails.id}
      configKey={K.AgGridTable.Keys.JobDetailCandidateColumn}
      getData={getServerSideData}
      onDataFetchSuccess={onGridDataFetchSuccess}
      defaultColumnConfig={columns}
      rowSelection="multiple"
      onSelectionChanged={onSelectionChanged}
      getRowStyle={getRowStyle}
      isRowSelectable={isRowSelectable}
      onRowSelected={onRowSelected}
      onRowClicked={onRowClicked}
    />
  );
}
